// cabinetSession — client-side storage of a claimed Cabinet d'étude admission
// (« la porte du Cabinet », access circle 2 —
// memory/strategie/CENTRE_DE_RECHERCHE.md, backend/cabinet_service.py).
//
// A researcher who claims a Cabinet code receives {holder_name, institution,
// expires_at, token} from POST /api/medard/cabinet/claim. This module persists
// that admission and answers ONE question for the UI: « is a researcher
// admitted on this device? ».
//
// ─────────────────────────────────────────────────────────────────────────
// WHY THIS IS A SEPARATE MODULE AND NOT A FLAG ON staySession
// ─────────────────────────────────────────────────────────────────────────
// A Cabinet code admits its holder to the SCHOLARLY REGISTER — nothing else.
// It is NOT a stay: it must never open « La Clef », the suite, the private
// concierge or any guest surface of the Manoir tab. Those all gate on
// staySession/useStaySession, so a Cabinet admission is stored under its OWN
// key, emits its OWN event, and is read by its OWN hook. Only the Professeur
// control ORs the two. A reading room is not a bedroom.
//
// Same discipline as staySession, deliberately — one access grammar for the
// whole house:
//   • the clock is injected (`now`) so expiry is testable without timers;
//   • the storage is injected so tests never touch the real localStorage;
//   • load() never throws — corrupt JSON, missing storage (Safari private
//     mode) or an expired admission all collapse to null;
//   • we store ONLY what the endpoint returned — never a derived privilege.
//
// Expiry mirrors the backend: the token carries the code's `expires_at`, so
// the session dies at that same instant — we never keep a corpse the backend
// would reject anyway. The SERVER remains the only truth: it re-reads the DB
// on every question, so a revoked code loses the register immediately even
// though this stored session still looks alive.

export const CABINET_STORAGE_KEY = 'medard-cabinet';

// Fired on this tab whenever the Cabinet is claimed or ended, so any component
// (the Professeur control, the Cabinet card) can react without prop-drilling.
// Same-tab `storage` events do not fire, hence our own event.
export const CABINET_EVENT = 'medard-cabinet-change';

function emitCabinetChange() {
    try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new Event(CABINET_EVENT));
        }
    } catch {
        /* SSR / jsdom without Event — callers still get their return value */
    }
}

/** Default storage — guarded: Safari private mode may throw on access. */
function defaultStorage() {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
}

/** Millisecond timestamp of `now` (Date | number | undefined → Date.now()). */
function toMs(now) {
    if (now instanceof Date) return now.getTime();
    if (Number.isFinite(now)) return now;
    return Date.now();
}

/**
 * True while the admission is live: shaped like a claim payload AND before
 * `expires_at`. Pure — no I/O, clock injected.
 *
 * `expires_at` may legitimately be null (cabinet_service.public_view returns
 * null when the code document carries no expiry — « perpetual until revoked »).
 * A null expiry is therefore NOT a malformed payload: the admission stands,
 * and revocation — re-checked server-side on every question — is the control.
 * An unparseable non-null date is treated as absent, i.e. NOT active: a
 * privilege must never ride a doubt.
 */
export function isCabinetActive(session, now = undefined) {
    if (!session || typeof session !== 'object') return false;
    if (!session.token) return false;
    if (session.expires_at === null || session.expires_at === undefined || session.expires_at === '') {
        return true;
    }
    const expires = Date.parse(session.expires_at);
    if (!Number.isFinite(expires)) return false;
    return toMs(now) < expires;
}

/**
 * Persist a freshly claimed Cabinet admission. Returns the stored session
 * (with `claimed_at`) or null when the payload is not a valid claim response
 * or the storage is unavailable — callers can degrade to in-memory state.
 *
 * ONLY the endpoint's own fields are kept. Nothing here ever carries a suite,
 * a guest name or a checkout: a researcher has no room.
 */
export function saveCabinetSession(payload, { now, storage = defaultStorage() } = {}) {
    if (!payload || typeof payload !== 'object') return null;
    const session = {
        token: payload.token || '',
        holder_name: payload.holder_name || '',
        institution: payload.institution || '',
        expires_at: payload.expires_at || null,
        claimed_at: new Date(toMs(now)).toISOString(),
    };
    if (!session.token) return null;
    if (!isCabinetActive(session, now)) return null; // never store a dead admission
    try {
        storage?.setItem(CABINET_STORAGE_KEY, JSON.stringify(session));
    } catch {
        /* quota / private mode — the caller keeps the in-memory session */
    }
    emitCabinetChange();
    return session;
}

/**
 * The active Cabinet admission stored on this device, or null. An expired or
 * corrupt entry is removed on read (self-cleaning: no stale Professeur past
 * the code's expiry). Never throws.
 */
export function loadCabinetSession({ now, storage = defaultStorage() } = {}) {
    let raw = null;
    try {
        raw = storage?.getItem(CABINET_STORAGE_KEY) ?? null;
    } catch {
        return null;
    }
    if (!raw) return null;
    let session = null;
    try {
        session = JSON.parse(raw);
    } catch {
        session = null;
    }
    if (!isCabinetActive(session, now)) {
        clearCabinetSession({ storage });
        return null;
    }
    return session;
}

/** Forget the admission (« Quitter le Cabinet », or self-cleaning on expiry). */
export function clearCabinetSession({ storage = defaultStorage() } = {}) {
    try {
        storage?.removeItem(CABINET_STORAGE_KEY);
    } catch {
        /* noop */
    }
    emitCabinetChange();
}
